import React, { useState } from 'react';
import { DollarSign, Clock, Flame, TrendingUp, Users, ChevronRight } from 'lucide-react';
import Sparkline from './Sparkline';
import { sans, mono, green, greenLight, greenDark, red, amber, useTheme } from '../../tokens';

// INV Phase-2 -- KPI hero strip with icons, sparklines, and click-to-scroll

export default function KPIHero({ liquidity, runway, burn, capitalRaised, headcount, sparkData, cc }) {
  const t = useTheme();
  const [hoveredCard, setHoveredCard] = useState(null);

  // Compute runway color based on numeric months
  const runwayMonths = typeof runway === 'string' ? parseInt(runway, 10) : (runway || 0);
  const runwayColor = runwayMonths > 24 ? green : runwayMonths > 12 ? amber : red;

  // Subtitles for capital and headcount
  const capitalSub = 'Seed + Series A -- Cumulative';
  const hcSub = 'Active employees -- Live (HR)';

  const cards = [
    { key: 'liquidity', label: 'Total Liquidity', icon: DollarSign, value: liquidity, color: green, target: 'inv-capital', sub: 'Cash + GICs -- Live (GL)', sparkColor: green },
    { key: 'runway', label: 'Runway Until', icon: Clock, value: runway, color: runwayColor, target: 'inv-capital', sub: `${runwayMonths} months at current burn`, sparkColor: runwayColor },
    { key: 'burn', label: 'Avg Monthly Burn', icon: Flame, value: burn, color: red, target: 'inv-metrics', sub: '6-month average -- Live (GL)', sparkColor: red },
    { key: 'capital', label: 'Capital Raised', icon: TrendingUp, value: capitalRaised, color: t.textPrimary, target: 'inv-capital', sub: capitalSub, sparkColor: '#3B82F6' },
    { key: 'headcount', label: 'Headcount', icon: Users, value: headcount, color: t.textPrimary, target: 'inv-headcount', sub: hcSub, sparkColor: '#3B82F6' },
  ];

  function scrollToSection(targetId) {
    const el = document.getElementById(targetId);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(5, 1fr)',
      gap: 14,
    }}>
      {cards.map(card => {
        const Icon = card.icon;
        const isHovered = hoveredCard === card.key;
        const sd = sparkData && sparkData[card.key];

        return (
          <div
            key={card.key}
            onMouseEnter={() => setHoveredCard(card.key)}
            onMouseLeave={() => setHoveredCard(null)}
            onClick={() => scrollToSection(card.target)}
            style={{
              background: `linear-gradient(135deg, ${t.bgCard}, ${t.bgSurface})`,
              border: `1px solid ${t.border}`,
              borderRadius: 10,
              padding: '16px 18px',
              cursor: 'pointer',
              transition: 'box-shadow 0.2s, transform 0.2s',
              boxShadow: isHovered ? '0 4px 12px rgba(0,0,0,0.08)' : 'none',
              transform: isHovered ? 'translateY(-2px)' : 'none',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Top row: icon + label */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <Icon size={18} color={card.color} style={{ opacity: 0.6 }} />
              <span style={{
                fontSize: 9, fontWeight: 600, textTransform: 'uppercase',
                letterSpacing: 0.6, color: t.textMuted, fontFamily: sans,
              }}>
                {card.label}
              </span>
            </div>

            {/* Main number */}
            <div style={{
              fontSize: 26, fontWeight: 700, fontFamily: mono,
              color: t.textPrimary, lineHeight: 1.1, marginBottom: 4,
            }}>
              {card.value}
            </div>

            {/* Subtitle */}
            <div style={{
              fontSize: 9, fontFamily: sans, color: t.textMuted,
              marginBottom: 8,
            }}>
              {card.sub}
            </div>

            {/* Sparkline */}
            {sd && sd.length >= 2 && (
              <div style={{ marginBottom: 6 }}>
                <Sparkline
                  data={sd}
                  color={card.sparkColor}
                  width={140}
                  height={20}
                  fill={true}
                  strokeWidth={1.5}
                />
              </div>
            )}

            {/* View details on hover */}
            <div style={{
              fontSize: 9, fontWeight: 500, fontFamily: sans,
              color: green,
              opacity: isHovered ? 1 : 0,
              transition: 'opacity 0.2s',
              display: 'flex', alignItems: 'center', gap: 2,
            }}>
              View details <ChevronRight size={10} />
            </div>
          </div>
        );
      })}
    </div>
  );
}
